import React, { useState, useEffect } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export default function FamilyIncomeCertificate() {
  const storedSessionId = localStorage.getItem("currentSessionId");
  const [selectedSession] = useState(storedSessionId || "");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");

  // Certificate fields
  const [studentName, setStudentName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [className, setClassName] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [totalIncome, setTotalIncome] = useState("");
  const [familyMembers, setFamilyMembers] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [template, setTemplate] = useState("Template1");
  const [isProcessing, setIsProcessing] = useState(false);

  // Font customization
  const [headingFontFamily, setHeadingFontFamily] = useState("Times New Roman");
  const [headingFontSize, setHeadingFontSize] = useState(30);
  const [headingFontColor, setHeadingFontColor] = useState("#1e3a8a");
  const [bodyFontFamily, setBodyFontFamily] = useState("Times New Roman");
  const [bodyFontSize, setBodyFontSize] = useState(18);
  const [bodyFontColor, setBodyFontColor] = useState("#000000");
  const [textAlign, setTextAlign] = useState("center");

  const labelStyle = "block font-semibold text-black text-[18px] mb-2";
  const inputStyle =
    "w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-800 text-[14px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  // ==========================
  // Fetch Classes
  // ==========================
  useEffect(() => {
    if (!selectedSession) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${selectedSession}`)
      .then((res) => setClasses(res.data))
      .catch((err) => console.error("Error fetching classes:", err));
  }, [selectedSession]);

  // ==========================
  // Fetch Sections
  // ==========================
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(
          selectedClass
        )}/getSectionsByClassName?sessionId=${selectedSession}`
      )
      .then((res) => setSections(res.data))
      .catch((err) => console.error("Error fetching sections:", err));
  }, [selectedClass, selectedSession]);

  // ==========================
  // Fetch Students
  // ==========================
  useEffect(() => {
    if (!selectedClass || !selectedSection) {
      setStudents([]);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
        params: {
          class: selectedClass,
          section: selectedSection,
          sessionId: selectedSession,
          status: "active",
        },
      })
      .then((res) => setStudents(res.data))
      .catch((err) => console.error("Error fetching students:", err));
  }, [selectedClass, selectedSection, selectedSession]);

  // ==========================
  // Auto-fill student info
  // ==========================
  useEffect(() => {
    if (!selectedStudent) return;
    const stu = students.find((s) => s._id === selectedStudent);
    if (stu) {
      setStudentName(`${stu.firstName} ${stu.lastName}`);
      setFatherName(stu.fatherName || "");
      setClassName(stu.class || selectedClass);
      setSectionName(stu.section || selectedSection);
    }
  }, [selectedStudent, students]);

  // ==========================
  // Download PDF
  // ==========================
  const handleDownloadPDF = async () => {
    const certificateElement = document.getElementById("certificate-preview");
    const canvas = await html2canvas(certificateElement, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);
    pdf.save("family_income_certificate.pdf");
  };

  // ==========================
  // Save Certificate
  // ==========================
  const handleSaveCertificate = async () => {
    if (isProcessing) return; 
    setIsProcessing(true);
    try {
      if (!selectedStudent) {
        alert("Please select a student");
        return;
      }

      const certificateElement = document.getElementById("certificate-preview");
      const canvas = await html2canvas(certificateElement, { scale: 2, useCORS: true });
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pdfWidth, imgHeight);

      const pdfBlob = pdf.output("blob");
      const file = new File([pdfBlob], "family_income_certificate.pdf", { type: "application/pdf" });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("studentId", selectedStudent);
      formData.append("certificateType", "FamilyIncome");
      formData.append("sessionId", selectedSession);
      formData.append("familyMembers", familyMembers);
      formData.append("totalIncome", totalIncome);

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      alert("Certificate created successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to create certificate");
    }
    setIsProcessing(false);
  };

  // ==========================
  // Templates
  // ==========================
  const Template1 = () => (
    <div
      className="p-10 bg-white shadow-xl w-full border-[12px] border-[#d4af37] rounded-xl text-center"
      style={{
        fontFamily: headingFontFamily,
        fontSize: headingFontSize,
        color: headingFontColor,
        textAlign: textAlign,
      }}
    >
      <h1 className="text-3xl font-bold text-blue-900">Family Income Certificate</h1>
      <p className="mt-4 text-lg text-gray-700">This is to certify that</p>
      <h2 className="text-3xl font-semibold mt-2 underline decoration-blue-900">
        {fatherName || "Name Surname"}
      </h2>
      <p className="mt-4 text-gray-700">
        is the head of a family consisting of <strong>{familyMembers || "_"}</strong> members.
      </p>
      <p className="mt-4 text-gray-700">Total Monthly Income:</p>
      <h3 className="text-2xl font-bold text-green-600">
        {totalIncome ? `₹${Number(totalIncome).toLocaleString()}` : "₹___"}
      </h3>
      <p className="mt-6">Issued on: {date}</p>
      <div className="mt-12">
        <p className="border-t-2 border-gray-700 w-64 mx-auto"></p>
        <p className="mt-2">Authorized Signature</p>
      </div>
    </div>
  );

  const Template2 = () => (
    <div className="relative bg-white p-12 shadow-2xl rounded-xl border-[14px] border-[#1e3a8a] overflow-hidden text-center">
      <div className="absolute left-0 top-0 h-full w-6 bg-[#1e3a8a]"></div>
      <div className="absolute right-0 top-0 h-full w-6 bg-[#fbbf24]"></div>
      <h1 className="text-5xl font-extrabold text-[#1e3a8a]">FAMILY INCOME CERTIFICATE</h1>
      <p className="mt-4 text-lg text-gray-700">This certifies that</p>
      <h2 className="text-4xl font-bold mt-4 text-[#f59e0b] underline decoration-[#1e3a8a]">
        {fatherName || "Name Surname"}
      </h2>
      <p className="mt-6 text-lg text-gray-700">
        is the head of a family of <strong>{familyMembers || "__"}</strong> members.
      </p>
      <p className="mt-6 text-lg text-gray-700">Total Monthly Income:</p>
      <h3 className="text-3xl font-semibold text-green-700">
        {totalIncome ? `₹${Number(totalIncome).toLocaleString()}` : "₹___"}
      </h3>
      <p className="mt-8 text-gray-700">Issued on: {date}</p>
      <div className="mt-12">
        <p className="border-t-2 border-gray-700 w-60 mx-auto"></p>
        <p className="mt-2 font-semibold">Authorized Signature</p>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-4 bg-[#1e3a8a]"></div>
    </div>
  );

  const Template3 = () => (
    <div className="relative bg-white p-10 shadow-2xl rounded-xl border-[10px] border-[#a16b3d] overflow-hidden">
      <div className="absolute top-0 left-0 w-24 h-24 bg-[#a16b3d] rotate-45 origin-top-left"></div>
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#d4af37] rotate-45 origin-top-right"></div>
      <div className="relative z-10 text-center">
        <h1 className="text-4xl font-bold text-gray-800 uppercase">Family Income Certificate</h1>
        <p className="mt-4 text-gray-700">This certifies that</p>
        <h2 className="text-5xl font-extrabold mt-4 text-[#a16b3d]">
          {fatherName || "Name Surname"}
        </h2>
        <p className="mt-4 text-gray-700">
          is the head of a family consisting of <strong>{familyMembers || "__"}</strong> members.
        </p>
        <p className="mt-6 font-semibold">Total Monthly Income:</p>
        <h3 className="text-3xl font-bold text-green-700">
          {totalIncome ? `₹${Number(totalIncome).toLocaleString()}` : "₹___"}
        </h3>
        <p className="mt-8 text-gray-700">Issued on: {date}</p>
        <div className="mt-12">
          <p className="border-t-2 border-gray-700 w-48 mx-auto"></p>
          <p className="mt-2 font-semibold">Authorized Signature</p>
        </div>
      </div>
    </div>
  );

  // ==========================
  // JSX
  // ==========================
  return (
    <div className="p-6 bg-white shadow-md rounded-lg w-full mx-auto max-w-screen-xl">
      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className={labelStyle}>Class</label>
          <select
            className={inputStyle}
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Select Class</option>
            {classes.map((cls) => (
              <option key={cls._id} value={cls.className}>
                {cls.className}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelStyle}>Section</label>
          <select
            className={inputStyle}
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
          >
            <option value="">Select Section</option>
            {sections.map((sec) => (
              <option key={sec._id} value={sec.sectionName}>
                {sec.sectionName}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelStyle}>Student</label>
          <select
            className={inputStyle}
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
          >
            <option value="">Select Student</option>
            {students.map((stu) => (
              <option key={stu._id} value={stu._id}>
                {stu.firstName} {stu.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Family Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className={labelStyle}>Family Members</label>
          <input
            type="number"
            className={inputStyle}
            value={familyMembers}
            onChange={(e) => setFamilyMembers(e.target.value)}
          />
        </div>
        <div>
          <label className={labelStyle}>Total Monthly Income</label>
          <input
            type="number"
            className={inputStyle}
            value={totalIncome}
            onChange={(e) => setTotalIncome(e.target.value)}
          />
        </div>
      </div>

      {/* Template & Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className={labelStyle}>Select Template</label>
          <select className={inputStyle} value={template} onChange={(e) => setTemplate(e.target.value)}>
            <option value="Template1">Template 1</option>
            <option value="Template2">Template 2</option>
            <option value="Template3">Template 3</option>
          </select>
        </div>
        <div>
          <label className={labelStyle}>Issued Date</label>
          <input type="date" className={inputStyle} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      {/* Certificate Preview */}
      <div id="certificate-preview" className="mt-6">
        {template === "Template1" && <Template1 />}
        {template === "Template2" && <Template2 />}
        {template === "Template3" && <Template3 />}
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-4 mt-6">
        <button
          onClick={handleDownloadPDF}
          className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-lg hover:bg-green-700"
        >
          Download PDF
        </button>
        <button
          onClick={handleSaveCertificate}
          className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:bg-blue-700"
        >
          Create Certificate
        </button>
      </div>
    </div>
  );
}
